<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import {
  PlusIcon,
  HomeIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/vue/24/outline";
import { useAuth } from "../composables/useAuth";
import { usePreferences } from "../composables/usePreferences";
import { useToast } from "../composables/useToast";
import HelpOverlay from "./HelpOverlay.vue";

/**
 * Sidebar — navigation only.
 *
 * Per the polish design doc (Decision #5), the job list lives in the
 * main area on `/`, not here. The sidebar is fixed-height and never
 * grows: brand · + New Job · nav links · user footer. This is what
 * lets us scale to hundreds of jobs without redesigning the layout
 * when wishlist features land.
 */

const router = useRouter();
const { user, signOut } = useAuth();
const {
  preferences,
  saveError,
  loadPreferences,
  setEmailNotificationsEnabled,
} = usePreferences();
const { showError } = useToast();

const helpOpen = ref(false);

// Fetch on mount so the checkbox renders with the persisted value
// rather than the default. The user can sign out before this resolves;
// the composable handles the 401 → signOut path.
onMounted(loadPreferences);

// Surface a transient failure to the user without leaving the
// checkbox in a "looks toggled but isn't" state — the composable
// already rolled back the optimistic flip.
watch(saveError, (err) => {
  if (err) showError(`Couldn't save preferences: ${err}`);
});

async function onToggleEmailNotifications(event: Event) {
  const checked = (event.target as HTMLInputElement).checked;
  await setEmailNotificationsEnabled(checked);
}

function goNew() {
  router.push({ name: "new-job" });
}
function goHome() {
  router.push({ name: "home" });
}
async function handleSignOut() {
  await signOut();
}

/**
 * The user's avatar comes from the Google id_token `picture` claim
 * (added to the session in step 7). Fall back to a circle with the
 * email's first letter when the URL is missing OR when the <img>
 * fails to load (network error, expired Google CDN URL, etc.).
 */
const avatarFailed = ref(false);
const avatarInitial = computed(() => (user.value?.email ?? "?").charAt(0).toUpperCase());
const showImg = computed(
  () => !!user.value?.picture && !avatarFailed.value,
);
</script>

<template>
  <aside
    data-testid="sidebar"
    class="w-60 shrink-0 border-r border-border bg-bg-main flex flex-col h-screen"
  >
    <header class="px-4 py-4 border-b border-border">
      <h1 class="text-base font-semibold text-text-primary">Rally Segmentation</h1>
    </header>

    <div class="px-3 py-3 border-b border-border">
      <button
        type="button"
        data-testid="new-job-button"
        class="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-hover transition-colors"
        @click="goNew"
      >
        <PlusIcon class="h-4 w-4" aria-hidden="true" />
        New Job
      </button>
    </div>

    <nav class="flex-1 px-3 py-3 space-y-1" aria-label="Primary">
      <button
        type="button"
        data-testid="nav-home"
        class="w-full text-left inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
        @click="goHome"
      >
        <HomeIcon class="h-4 w-4" aria-hidden="true" />
        Home
      </button>
      <button
        type="button"
        data-testid="nav-help"
        class="w-full text-left inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
        @click="helpOpen = true"
      >
        <QuestionMarkCircleIcon class="h-4 w-4" aria-hidden="true" />
        Help
      </button>
    </nav>

    <!--
      Settings — V0 has one toggle (email notifications). Sits right
      above the user footer so it's adjacent to the email it's
      affecting. The checkbox uses native <input> for keyboard /
      screen-reader behaviour out of the box; styling is minimal.
    -->
    <section
      class="px-3 py-3 border-t border-border"
      aria-labelledby="sidebar-settings-heading"
    >
      <h2
        id="sidebar-settings-heading"
        class="px-3 mb-1.5 text-[10px] uppercase tracking-wider text-text-tertiary"
      >
        Settings
      </h2>
      <label
        class="flex items-center gap-2 px-3 py-1.5 text-sm text-text-secondary cursor-pointer rounded-md hover:bg-bg-hover hover:text-text-primary transition-colors"
        data-testid="sidebar-pref-email-notifications-label"
      >
        <input
          type="checkbox"
          data-testid="sidebar-pref-email-notifications"
          class="h-4 w-4 rounded border-border accent-primary"
          :checked="preferences.email_notifications_enabled"
          @change="onToggleEmailNotifications"
        />
        Email me when jobs finish
      </label>
    </section>

    <footer
      v-if="user"
      data-testid="sidebar-user-info"
      class="px-3 py-3 border-t border-border flex items-center gap-2"
    >
      <img
        v-if="showImg"
        data-testid="sidebar-user-avatar"
        :src="user.picture!"
        :alt="user.email"
        class="h-8 w-8 rounded-full object-cover shrink-0"
        @error="avatarFailed = true"
      />
      <div
        v-else
        data-testid="sidebar-user-avatar-fallback"
        class="h-8 w-8 rounded-full bg-bg-secondary text-text-secondary flex items-center justify-center text-sm font-medium shrink-0"
        aria-hidden="true"
      >
        {{ avatarInitial }}
      </div>
      <span
        data-testid="sidebar-user-email"
        class="flex-1 truncate text-xs text-text-secondary"
      >{{ user.email }}</span>
      <button
        type="button"
        data-testid="sign-out-button"
        class="shrink-0 rounded-md p-1.5 text-text-tertiary hover:bg-bg-hover hover:text-text-primary transition-colors"
        aria-label="Sign out"
        title="Sign out"
        @click="handleSignOut"
      >
        <ArrowRightOnRectangleIcon class="h-4 w-4" aria-hidden="true" />
      </button>
    </footer>

    <HelpOverlay :open="helpOpen" @close="helpOpen = false" />
  </aside>
</template>
